import React, { useState } from 'react';
import { useLinkedIn } from '../../context/LinkedInContext';
import BaseModal from '../modals/BaseModal';
import {
  parseLinkedInCsvFile,
  parseLinkedInProspects
} from '../../utils/linkedinImportParser';

const emptyState = { text: '', rows: [], result: null, error: '' };

const buildLinkedInSearchUrl = (companyName) => {
  const roles = [
    'Daglig leder',
    'CEO',
    'HR',
    'HR-leder',
    'HMS',
    'HSE',
    'COO',
    'Operations'
  ];

  return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(
    `${companyName} ${roles.join(' ')}`
  )}`;
};

const ImportProspectsModal = ({
  isOpen,
  onClose,
  initialCompanyName = '',
  sourceNote = ''
}) => {
  const {
    prospects,
    addProspect,
    createCompanyResearchProspect
  } = useLinkedIn();
  const [method, setMethod] = useState('paste');
  const [state, setState] = useState(emptyState);
  const [isImporting, setIsImporting] = useState(false);
  const [isCreatingResearch, setIsCreatingResearch] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [copyFallbackUrl, setCopyFallbackUrl] = useState('');

  const linkedinSearchUrl = initialCompanyName.trim()
    ? buildLinkedInSearchUrl(initialCompanyName.trim())
    : '';

  const applyContext = (rows) => rows.map((row) => {
    const contextNote = sourceNote.trim();
    const existingNotes = typeof row.notes === 'string' ? row.notes.trim() : '';

    let notes = row.notes || '';

    if (contextNote && !existingNotes) {
      notes = contextNote;
    } else if (contextNote && !existingNotes.includes(contextNote)) {
      notes = `${existingNotes}\n\n${contextNote}`;
    }

    return {
      ...row,
      company_name: row.company_name || initialCompanyName,
      notes
    };
  });

  const updatePreview = (text) => {
    const rows = parseLinkedInProspects(text, prospects);
    setState({
      ...emptyState,
      text,
      rows: applyContext(rows)
    });
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const rows = await parseLinkedInCsvFile(file, prospects);
      setState({
        ...emptyState,
        rows: applyContext(rows),
        text: file.name
      });
    } catch {
      setState({ ...emptyState, error: 'CSV-filen kunne ikke leses.' });
    }
  };

  const handleCopyLinkedInSearch = async () => {
    if (!linkedinSearchUrl) return;

    setCopyMessage('');
    setCopyFallbackUrl('');

    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error('Clipboard API unavailable');
      }

      await navigator.clipboard.writeText(linkedinSearchUrl);
      setCopyMessage('LinkedIn-søk kopiert');
    } catch {
      setCopyMessage('Kunne ikke kopiere automatisk. Kopier lenken manuelt:');
      setCopyFallbackUrl(linkedinSearchUrl);
    }
  };

  const handleCreateResearchProspect = async () => {
    if (!initialCompanyName.trim() || isCreatingResearch) return;

    setIsCreatingResearch(true);
    setState(current => ({ ...current, error: '', result: null }));

    const result = await createCompanyResearchProspect({
      companyName: initialCompanyName,
      orgNumber: sourceNote.match(/orgnr\s+([0-9]+)/i)?.[1] || ''
    });

    setIsCreatingResearch(false);

    if (result?.success) {
      setState(current => ({
        ...current,
        result: {
          imported: 1,
          skipped: 0,
          failed: 0,
          message: 'LinkedIn research-prospect opprettet'
        }
      }));
      return;
    }

    setState(current => ({
      ...current,
      error: result?.duplicate
        ? 'LinkedIn-prospect finnes allerede'
        : result?.error || 'Research-prospectet kunne ikke opprettes.'
    }));
  };

  const handleImport = async () => {
    const readyRows = state.rows.filter((row) => row.status === 'Klar');

    if (!readyRows.length) {
      setState((current) => ({
        ...current,
        error: 'Ingen gyldige prospekter funnet.'
      }));
      return;
    }

    setIsImporting(true);

    const results = await Promise.all(readyRows.map(async (row) => {
      try {
        return await addProspect({
          full_name: row.full_name,
          title: row.title,
          company_name: row.company_name,
          linkedin_url: row.linkedin_url,
          priority: row.priority,
          notes: row.notes
        });
      } catch {
        return {
          success: false,
          error: 'Prospektet kunne ikke behandles.'
        };
      }
    }));

    const imported = results.filter((result) => result?.success === true).length;
    const failed = results.length - imported;
    const skipped = state.rows.length - readyRows.length;

    setIsImporting(false);
    setState((current) => ({
      ...current,
      result: { imported, skipped, failed },
      error: failed ? 'Noen rader ble hoppet over.' : ''
    }));
  };

  const handleClose = () => {
    setState(emptyState);
    setMethod('paste');
    setIsImporting(false);
    setIsCreatingResearch(false);
    setCopyMessage('');
    setCopyFallbackUrl('');
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importer LinkedIn-prospekter"
      size="2xl"
      disableAnimation={true}
    >
      <div className="space-y-4">
        {initialCompanyName && (
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
            <p className="font-bold">BRREG-selskap: {initialCompanyName}</p>
            <p className="mt-1">
              Lim inn LinkedIn-profiler du selv har funnet for dette selskapet.
            </p>
          </div>
        )}

        {linkedinSearchUrl && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-bold text-slate-700">
              Finn personer manuelt på LinkedIn
            </p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <a
                href={linkedinSearchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-center text-xs font-bold text-blue-700 hover:bg-blue-100"
              >
                Åpne LinkedIn-søk
              </a>
              <button
                type="button"
                onClick={handleCopyLinkedInSearch}
                className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-bold text-blue-700 hover:bg-blue-50"
              >
                Kopier LinkedIn-søk
              </button>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              Hvis LinkedIn blokkeres i preview, kopier lenken og åpne den i ny fane.
            </p>
            {(copyMessage || copyFallbackUrl) && (
              <div className="mt-2">
                {copyMessage && (
                  <p className="text-xs font-semibold text-slate-600" role="status">
                    {copyMessage}
                  </p>
                )}
                {copyFallbackUrl && (
                  <input
                    type="text"
                    readOnly
                    value={copyFallbackUrl}
                    onFocus={(event) => event.target.select()}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600"
                    aria-label="LinkedIn-søkeadresse"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {initialCompanyName && (
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3">
            <p className="text-sm font-bold text-indigo-900">
              Finner du ikke en person ennå?
            </p>
            <p className="mt-1 text-xs leading-relaxed text-indigo-800">
              Opprett en company-level research-oppgave. Den lagres i Prospect Inbox og Dagens Outreach.
            </p>
            <button
              type="button"
              onClick={handleCreateResearchProspect}
              disabled={isCreatingResearch}
              className="mt-3 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isCreatingResearch
                ? 'Oppretter research-prospect...'
                : 'Opprett research-prospect for dette selskapet'}
            </button>
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMethod('paste')}
            className={`px-4 py-2 rounded-lg text-sm font-bold ${method === 'paste' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}
          >
            Lim inn liste
          </button>
          <button
            type="button"
            onClick={() => setMethod('csv')}
            className={`px-4 py-2 rounded-lg text-sm font-bold ${method === 'csv' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}
          >
            CSV
          </button>
        </div>

        {method === 'paste' ? (
          <textarea
            rows="6"
            value={state.text}
            onChange={(event) => updatePreview(event.target.value)}
            placeholder={'Lim inn LinkedIn-profil-URL-er du selv har funnet, én per linje.\nEksempel:\nOla Nordmann; Daglig leder; ; https://www.linkedin.com/in/ola-nordmann'}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
          />
        ) : (
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFile}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm"
          />
        )}

        {state.error && (
          <p className="text-sm font-semibold text-rose-600" role="alert">
            {state.error}
          </p>
        )}

        {state.rows.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Navn</th>
                  <th className="px-3 py-2">Tittel</th>
                  <th className="px-3 py-2">Selskap</th>
                  <th className="px-3 py-2">LinkedIn URL</th>
                  <th className="px-3 py-2">Prioritet</th>
                  <th className="px-3 py-2">Merknad</th>
                </tr>
              </thead>
              <tbody>
                {state.rows.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-bold">{row.status}</td>
                    <td className="px-3 py-2">{row.full_name || '—'}</td>
                    <td className="px-3 py-2">{row.title || '—'}</td>
                    <td className="px-3 py-2">{row.company_name || '—'}</td>
                    <td className="px-3 py-2">{row.linkedin_url || '—'}</td>
                    <td className="px-3 py-2">{row.priority}</td>
                    <td className="px-3 py-2 text-rose-600">{row.error || 'Klar for import'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {state.result && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            {state.result.message || (
              <>
                Importert: <strong>{state.result.imported}</strong> · Hoppet over: <strong>{state.result.skipped}</strong> · Feil: <strong>{state.result.failed}</strong>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl border border-slate-200 px-4 py-2 font-bold text-slate-600"
          >
            Lukk
          </button>
          <button
            type="button"
            disabled={isImporting || !state.rows.some((row) => row.status === 'Klar')}
            onClick={handleImport}
            className="rounded-xl bg-blue-600 px-5 py-2 font-bold text-white shadow-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isImporting ? 'Importer...' : 'Importer godkjente prospekter'}
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

export default ImportProspectsModal;